import { EmbeddedStateCommittedEvent } from '@/url/events/EmbeddedStateCommittedEvent';
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { computed, ref } from 'vue';
import { EmbeddedStateIgnoredEvent } from '@/url/events/EmbeddedStateIgnoredEvent';
import { EventAggregationService } from './EventAggregationService';
import { injectable } from 'inversify';
import { ApplicationStatusEnum } from './ApplicationStatusEnum';
import { RequestInitiatedEvent } from './events/RequestInitiatedEvent';
import { RequestCompletedEvent } from './events/RequestCompletedEvent';
import { ApplicationStatusReadyEvent } from './events/ApplicationStatusReadyEvent';

@injectable()
export class ApplicationStatus implements
	ISubscriber<EmbeddedStateCommittedEvent>,
	ISubscriber<EmbeddedStateIgnoredEvent>,
	ISubscriber<RequestInitiatedEvent>,
	ISubscriber<RequestCompletedEvent> {
	private readonly eventAggregationService: EventAggregationService;
	private isEmbeddedStateReady = false;
	private loadingCounter = 0;

	private value = ref<ApplicationStatusEnum>(ApplicationStatusEnum.INITIALIZING);

	public constructor(eventAggregationService: EventAggregationService) {
		this.eventAggregationService = eventAggregationService;
		this.eventAggregationService.subscribeTo(EmbeddedStateCommittedEvent.EventType, this);
		this.eventAggregationService.subscribeTo(EmbeddedStateIgnoredEvent.EventType, this);
		this.eventAggregationService.subscribeTo(RequestInitiatedEvent.EventType, this);
		this.eventAggregationService.subscribeTo(RequestCompletedEvent.EventType, this);
	}

	public isReady = computed(() => {
		return this.value.value === ApplicationStatusEnum.READY;
	});

	public isInitializing = computed(() => {
		return this.value.value === ApplicationStatusEnum.INITIALIZING;
	});

	public isLoaded = computed(() => {
		return this.value.value === ApplicationStatusEnum.LOADED;
	});

	public isLoading = computed(() => {
		return this.value.value === ApplicationStatusEnum.LOADING;
	});

	public isBusy = computed(() => {
		return !this.isLoaded.value;
	});

	public notify(
		event: EmbeddedStateCommittedEvent | EmbeddedStateIgnoredEvent | RequestInitiatedEvent | RequestCompletedEvent
	): void {
		if (EmbeddedStateCommittedEvent.is(event) && event.isInitialNavigation) {
			this.isEmbeddedStateReady = true;
		}

		if (EmbeddedStateIgnoredEvent.is(event) && event.isInitialNavigation) {
			this.isEmbeddedStateReady = true;
		}

		if (RequestInitiatedEvent.is(event)) {
			this.loadingCounter++;
		}

		if (RequestCompletedEvent.is(event)) {
			this.loadingCounter--;
		}

		const canTransitionToReady = this.isInitializing.value;
		if (canTransitionToReady && this.isEmbeddedStateReady) {
			this.changeStatusTo(ApplicationStatusEnum.READY);
		}

		const canTransitionToLoading = this.isReady.value || this.isLoaded.value;
		if (canTransitionToLoading && this.loadingCounter > 0) {
			this.changeStatusTo(ApplicationStatusEnum.LOADING);
		}

		const canTransitionToLoaded = this.isReady.value || this.isLoading.value;
		if (canTransitionToLoaded && this.loadingCounter === 0) {
			this.changeStatusTo(ApplicationStatusEnum.LOADED);
		}
	}

	private changeStatusTo(status: ApplicationStatusEnum): void {
		if (this.value.value !== status) {
			this.value.value = status;

			if (status === ApplicationStatusEnum.READY) {
				this.eventAggregationService.publishTo(
					ApplicationStatusReadyEvent.EventType,
					new ApplicationStatusReadyEvent()
				);
			}
		}
	}
}
